/**
 * eReg Delegation Log Page
 *
 * Native delegation of authority (DOA) management.
 * Tracks team members, delegated tasks, required documents, and signatures.
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Plus,
  AlertCircle,
  CheckCircle,
  FileText,
  X,
  UserPlus,
  PenTool,
  Pencil,
  Download,
  Link2,
  Loader2,
  MoreVertical,
  Trash2,
  UserMinus,
  RotateCcw,
  ClipboardSignature,
} from 'lucide-react';
import { useEreg } from '../contexts/EregContext';
import { useAuth } from '../../contexts/AuthContext';
import * as eregApi from '../lib/api';
import { PageHeader, RefreshButton, fieldCls, primaryBtnCls, secondaryBtnCls, dangerBtnCls, useToast } from '../../ui';
import { formatDateOnly } from '../lib/dates';
import type { DelegationLog, DelegationTask, DelegationEntry, DelegationRequiredDoc, DelegationSignRole, User, SearchResult } from '../types';

// Entry status badge component ('terminated' dropped — the schema's status
// CHECK never allowed it; 'ended' is the closed state).
function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending_cert_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    pending_signatures: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    ended: 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300',
  };

  const labels: Record<string, string> = {
    pending_cert_review: 'Pending Docs',
    pending_signatures: 'Pending Signatures',
    active: 'Active',
    ended: 'Ended',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300'}`}>
      {labels[status] || status}
    </span>
  );
}

export function DelegationLogPage() {
  const { currentProtocol, isAdmin } = useEreg();
  const { user: authUser } = useAuth();
  const { toast } = useToast();
  const [delegationLog, setDelegationLog] = useState<DelegationLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showTaskEditor, setShowTaskEditor] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [signOffEntry, setSignOffEntry] = useState<{ entry: DelegationEntry; role: DelegationSignRole } | null>(null);
  const [reqDocsEntry, setReqDocsEntry] = useState<DelegationEntry | null>(null);
  const [editEntry, setEditEntry] = useState<DelegationEntry | null>(null);
  const [endEntry, setEndEntry] = useState<DelegationEntry | null>(null);
  const [wetInkEntry, setWetInkEntry] = useState<DelegationEntry | null>(null);
  const [entryMenuId, setEntryMenuId] = useState<string | null>(null);
  const entryMenuRef = useRef<HTMLDivElement | null>(null);
  const [exporting, setExporting] = useState(false);

  const isPi = Boolean(
    authUser?.id && currentProtocol?.primary_investigator_id === authUser.id
  );

  // Close the entry row menu on outside click
  useEffect(() => {
    if (!entryMenuId) return;
    const onMouseDown = (e: MouseEvent) => {
      if (entryMenuRef.current && !entryMenuRef.current.contains(e.target as Node)) {
        setEntryMenuId(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [entryMenuId]);

  useEffect(() => {
    if (currentProtocol?.id) {
      loadDelegationLog();
      loadRoles();
    }
  }, [currentProtocol?.id]);

  const loadDelegationLog = async () => {
    if (!currentProtocol?.id) return;
    setLoading(true);
    setLoadError(false);
    try {
      const log = await eregApi.getDelegationLog(currentProtocol.id);
      setDelegationLog(log);
      if (!log) {
        setShowSetupModal(true);
      }
    } catch (error) {
      console.error('Failed to load delegation log:', error);
      // A failed load must not render as "no log yet" (blank body / setup card).
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const roleList = await eregApi.getDelegationRoles();
      setRoles(roleList);
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  };

  const handleSetupDelegationLog = async () => {
    if (!currentProtocol?.id) return;
    try {
      const log = await eregApi.createDelegationLog({
        protocol_id: currentProtocol.id,
      });
      setDelegationLog(log);
      setShowSetupModal(false);
      loadDelegationLog(); // Reload to get tasks
    } catch (error: any) {
      console.error('Failed to create delegation log:', error);
      toast(error?.response?.data?.error || 'Failed to set up the delegation log', { type: 'error' });
    }
  };

  // C-9: DOA dates are DATE columns — parsing them as UTC midnight rendered
  // them a day early in US timezones. formatDateOnly treats them as calendar
  // dates.
  const formatDate = (dateString?: string) => formatDateOnly(dateString);

  const getTaskNumbers = (entry: DelegationEntry, tasks: DelegationTask[]) => {
    const delegatedTasks = tasks.filter(t => entry.delegated_task_ids.includes(t.id));
    return delegatedTasks.map(t => t.task_number).sort((a, b) => a - b).join(', ');
  };

  const getMissingDocsCount = (entry: DelegationEntry) => {
    return entry.required_docs?.filter(d => d.status === 'missing').length || 0;
  };

  const handleExportPdf = async () => {
    if (!currentProtocol?.id) return;
    setExporting(true);
    try {
      const blob = await eregApi.exportDelegationLogPdf(currentProtocol.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `delegation-log-${currentProtocol.protocol_number || 'protocol'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export delegation log PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  if (!currentProtocol) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <AlertCircle size={40} className="mx-auto mb-2 text-amber-500 dark:text-amber-400" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No Study Selected</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Please select a study from the dashboard to view its delegation log.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading delegation log...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <AlertCircle size={40} className="mx-auto mb-2 text-red-500 dark:text-red-400" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">Couldn't load the delegation log</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Something went wrong fetching this study's delegation log.
          </p>
          <button onClick={loadDelegationLog} className={secondaryBtnCls}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        title="Delegation Log"
        subtitle={`${currentProtocol.protocol_number} - ${currentProtocol.protocol_title || 'Untitled Study'}`}
        actions={
          <>
            {delegationLog && (
              <button
                onClick={handleExportPdf}
                disabled={exporting}
                className={secondaryBtnCls}
              >
                <Download size={16} />
                <span>{exporting ? 'Exporting...' : 'Export PDF'}</span>
              </button>
            )}
            {delegationLog && (
              <button
                onClick={() => setShowAddMemberModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
              >
                <UserPlus size={18} />
                <span>Add Team Member</span>
              </button>
            )}
            <RefreshButton onClick={loadDelegationLog} loading={loading} />
          </>
        }
      />

      {/* Missing-PI warning — with no PI configured, entries can reach
          "Pending Signatures" and then wait forever on a countersign nobody
          can perform. Say so instead of failing silently. */}
      {delegationLog && !currentProtocol.primary_investigator_id && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertCircle size={16} className="flex-shrink-0" />
          <span>
            No Primary Investigator is set for this study — PI countersigns are blocked until one is set.
          </span>
          {isAdmin && (
            <Link to="/ereg/protocols" className="font-medium underline">
              Set one in Manage Studies
            </Link>
          )}
        </div>
      )}

      {/* Setup Modal */}
      {showSetupModal && !delegationLog && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <Users size={48} className="mx-auto mb-4 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
            Set Up Delegation Log
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            The delegation log tracks team members and their authorized study responsibilities.
            Click below to set up the delegation log with default tasks.
          </p>
          <button
            onClick={handleSetupDelegationLog}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
          >
            Set Up Delegation Log
          </button>
        </div>
      )}

      {/* Delegation Log Content */}
      {delegationLog && (
        <>
          {/* Tasks Section */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <FileText size={20} className="text-primary-600 dark:text-primary-400" />
                Delegated Tasks
              </h2>
              {isAdmin && (
                <button onClick={() => setShowTaskEditor(true)} className={secondaryBtnCls}>
                  <Pencil size={14} />
                  Edit Tasks
                </button>
              )}
            </div>

            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(delegationLog.tasks || []).map((task) => (
                  <div
                    key={task.id}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg dark:bg-gray-900/40"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                      {task.task_number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{task.task_description}</p>
                      {task.required_document_types?.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Requires: {task.required_document_types.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Team Members Section */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Users size={20} className="text-primary-600 dark:text-primary-400" />
                Study Team ({delegationLog.entries?.length || 0})
              </h2>
            </div>

            {!delegationLog.entries || delegationLog.entries.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <Users size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                <p>No team members added yet</p>
                <p className="text-sm mt-1">Add team members to assign delegated tasks</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Team Member
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Role
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Tasks
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Dates
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Docs
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Sign-offs
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                    {delegationLog.entries.map((entry) => {
                      const missingDocs = getMissingDocsCount(entry);
                      return (
                        <tr key={entry.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {entry.user?.full_name || entry.external_name || 'Unknown'}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {entry.user?.email || entry.external_email}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {entry.role === 'Other' ? entry.role_other : entry.role}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded dark:bg-gray-700 dark:text-gray-200">
                              {getTaskNumbers(entry, delegationLog.tasks || [])}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <div className="text-gray-900 dark:text-gray-100">{formatDate(entry.start_date)}</div>
                            <div className="text-gray-500 dark:text-gray-400">
                              {entry.end_date ? `to ${formatDate(entry.end_date)}` : 'Ongoing'}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <StatusBadge status={entry.status} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={() => setReqDocsEntry(entry)}
                              className="rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                              title="View and link required documents"
                            >
                              {missingDocs > 0 ? (
                                <span className="flex items-center gap-1 text-sm font-medium text-amber-600 underline decoration-dotted underline-offset-2 transition-colors duration-150 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300">
                                  <AlertCircle size={14} />
                                  {missingDocs} missing — link docs
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-sm text-green-600 transition-colors duration-150 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300">
                                  <CheckCircle size={14} />
                                  Complete
                                </span>
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col gap-1 text-xs">
                              {entry.wet_ink_recorded_at ? (
                                // TMF-7: external member — the ceremony lives on
                                // the printed log; eReg records that it happened.
                                <span
                                  className="flex items-center gap-1 text-green-600 dark:text-green-400"
                                  title={`Wet-ink sign-offs recorded ${new Date(entry.wet_ink_recorded_at).toLocaleString()}${entry.wet_ink_note ? ` — "${entry.wet_ink_note}"` : ''}`}
                                >
                                  <CheckCircle size={12} />
                                  Wet-ink recorded
                                </span>
                              ) : (
                                <>
                                  {/* Team member start sign-off */}
                                  {entry.team_member_start_signature ? (
                                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400" title={`"${entry.team_member_start_signature.signature_meaning}" — ${new Date(entry.team_member_start_signature.signed_at).toLocaleString()}`}>
                                      <CheckCircle size={12} />
                                      Member: {entry.team_member_start_signature.signer_name}
                                    </span>
                                  ) : !entry.user_id ? (
                                    entry.status === 'pending_signatures' && (isAdmin || isPi) ? (
                                      <button
                                        onClick={() => setWetInkEntry(entry)}
                                        className="inline-flex w-fit items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                                        title="External members sign the printed log — record that the wet-ink ceremony happened"
                                      >
                                        <ClipboardSignature size={12} />
                                        Record wet-ink
                                      </button>
                                    ) : (
                                      <span
                                        className="text-gray-400 dark:text-gray-500"
                                        title="External team members cannot sign electronically — their sign-offs are recorded on the printed log by an administrator or the PI."
                                      >
                                        Member: signs the paper log
                                      </span>
                                    )
                                  ) : entry.status === 'pending_signatures' && authUser?.id && entry.user_id === authUser.id ? (
                                    <button
                                      onClick={() => setSignOffEntry({ entry, role: 'team_member' })}
                                      className="inline-flex w-fit items-center gap-1 rounded-lg bg-primary-600 px-2 py-1 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                      <PenTool size={12} />
                                      Sign
                                    </button>
                                  ) : (
                                    <span className="text-gray-400 dark:text-gray-500">Member: not signed</span>
                                  )}
                                  {/* PI start sign-off */}
                                  {entry.pi_start_signature ? (
                                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400" title={`"${entry.pi_start_signature.signature_meaning}" — ${new Date(entry.pi_start_signature.signed_at).toLocaleString()}`}>
                                      <CheckCircle size={12} />
                                      PI: {entry.pi_start_signature.signer_name}
                                    </span>
                                  ) : entry.status === 'pending_signatures' && isPi ? (
                                    <button
                                      onClick={() => setSignOffEntry({ entry, role: 'pi' })}
                                      className="inline-flex w-fit items-center gap-1 rounded-lg bg-primary-600 px-2 py-1 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                      <PenTool size={12} />
                                      Sign as PI
                                    </button>
                                  ) : (
                                    <span className="text-gray-400 dark:text-gray-500">PI: not signed</span>
                                  )}
                                </>
                              )}
                              {/* End-of-delegation evidence (C-11): shown once an
                                  entry is active/ended — monitors check this for
                                  departed staff. */}
                              {['active', 'ended'].includes(entry.status) && (
                                <>
                                  {entry.team_member_end_signature ? (
                                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400" title={`"${entry.team_member_end_signature.signature_meaning}" — ${new Date(entry.team_member_end_signature.signed_at).toLocaleString()}`}>
                                      <CheckCircle size={12} />
                                      End (member): {entry.team_member_end_signature.signer_name}
                                    </span>
                                  ) : entry.user_id && authUser?.id === entry.user_id && !entry.wet_ink_recorded_at ? (
                                    <button
                                      onClick={() => setSignOffEntry({ entry, role: 'team_member_end' })}
                                      className="inline-flex w-fit items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                                    >
                                      <PenTool size={12} />
                                      Sign end
                                    </button>
                                  ) : entry.status === 'ended' ? (
                                    <span className="text-gray-400 dark:text-gray-500">End (member): not signed</span>
                                  ) : null}
                                  {entry.pi_end_signature ? (
                                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400" title={`"${entry.pi_end_signature.signature_meaning}" — ${new Date(entry.pi_end_signature.signed_at).toLocaleString()}`}>
                                      <CheckCircle size={12} />
                                      End (PI): {entry.pi_end_signature.signer_name}
                                    </span>
                                  ) : isPi && !entry.wet_ink_recorded_at ? (
                                    <button
                                      onClick={() => setSignOffEntry({ entry, role: 'pi_end' })}
                                      className="inline-flex w-fit items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                                      title={entry.status === 'active' ? 'Signing the end of delegation closes this entry' : 'Add the PI end-of-delegation sign-off'}
                                    >
                                      <PenTool size={12} />
                                      {entry.status === 'active' ? 'End & sign' : 'Sign end as PI'}
                                    </button>
                                  ) : entry.status === 'ended' ? (
                                    <span className="text-gray-400 dark:text-gray-500">End (PI): not signed</span>
                                  ) : null}
                                </>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {(isAdmin || isPi) && (
                              <div className="relative inline-block">
                                <button
                                  onClick={() => setEntryMenuId(entryMenuId === entry.id ? null : entry.id)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700"
                                  aria-label={`Actions for ${entry.user?.full_name || entry.external_name || 'entry'}`}
                                >
                                  <MoreVertical size={16} />
                                </button>
                                {entryMenuId === entry.id && (
                                  <div
                                    ref={entryMenuRef}
                                    className="absolute right-0 top-9 z-20 w-56 rounded-xl border border-gray-200 bg-white py-1 text-left shadow-lg dark:border-gray-700 dark:bg-gray-800"
                                  >
                                    <button
                                      onClick={() => { setEntryMenuId(null); setEditEntry(entry); }}
                                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/50"
                                    >
                                      <Pencil size={14} />
                                      Edit entry
                                    </button>
                                    {entry.status === 'active' && (
                                      <button
                                        onClick={() => { setEntryMenuId(null); setEndEntry(entry); }}
                                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-red-600 transition-colors duration-150 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                                      >
                                        <UserMinus size={14} />
                                        End delegation
                                      </button>
                                    )}
                                    {entry.status === 'ended' && !entry.team_member_end_signature_id && !entry.pi_end_signature_id && (
                                      <button
                                        onClick={async () => {
                                          setEntryMenuId(null);
                                          try {
                                            await eregApi.updateDelegationEntry(delegationLog.id, entry.id, { status: 'active', end_date: null } as any);
                                            toast('Delegation reactivated');
                                            loadDelegationLog();
                                          } catch (err: any) {
                                            toast(err?.response?.data?.error || 'Failed to reactivate the entry', { type: 'error' });
                                          }
                                        }}
                                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/50"
                                      >
                                        <RotateCcw size={14} />
                                        Undo end
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Add Team Member Modal */}
      {showAddMemberModal && delegationLog && (
        <AddMemberModal
          delegationLog={delegationLog}
          roles={roles}
          siteId={currentProtocol.site_id}
          onClose={() => setShowAddMemberModal(false)}
          onSuccess={() => {
            setShowAddMemberModal(false);
            loadDelegationLog();
          }}
        />
      )}

      {/* Sign-off Modal */}
      {signOffEntry && delegationLog && (
        <SignOffModal
          delegationLogId={delegationLog.id}
          entry={signOffEntry.entry}
          role={signOffEntry.role}
          onClose={() => setSignOffEntry(null)}
          onSuccess={() => {
            setSignOffEntry(null);
            loadDelegationLog();
          }}
        />
      )}

      {/* Required-docs linking modal (audit S-2): the ONLY path from
          "Pending Docs" to a signable entry. */}
      {reqDocsEntry && delegationLog && (
        <RequiredDocsModal
          delegationLogId={delegationLog.id}
          entry={reqDocsEntry}
          tasks={delegationLog.tasks || []}
          siteId={currentProtocol.site_id}
          protocolId={currentProtocol.id}
          onClose={() => setReqDocsEntry(null)}
          onChanged={loadDelegationLog}
        />
      )}

      {/* Task editor (P-6: "Edit Tasks" was a pure no-op) */}
      {showTaskEditor && delegationLog && (
        <TaskEditorModal
          delegationLogId={delegationLog.id}
          tasks={delegationLog.tasks || []}
          onClose={() => setShowTaskEditor(false)}
          onSaved={() => {
            setShowTaskEditor(false);
            toast('Delegated tasks updated');
            loadDelegationLog();
          }}
        />
      )}

      {/* Edit entry (P-6: entries couldn't be edited at all) */}
      {editEntry && delegationLog && (
        <EditEntryModal
          delegationLogId={delegationLog.id}
          entry={editEntry}
          tasks={delegationLog.tasks || []}
          roles={roles}
          onClose={() => setEditEntry(null)}
          onSaved={() => {
            setEditEntry(null);
            toast('Entry updated');
            loadDelegationLog();
          }}
        />
      )}

      {/* End delegation (departed staff stop reading "Active" forever) */}
      {endEntry && delegationLog && (
        <EndEntryModal
          delegationLogId={delegationLog.id}
          entry={endEntry}
          onClose={() => setEndEntry(null)}
          onEnded={() => {
            setEndEntry(null);
            toast('Delegation ended');
            loadDelegationLog();
          }}
        />
      )}

      {/* TMF-7: record the external member's wet-ink ceremony */}
      {wetInkEntry && delegationLog && (
        <WetInkModal
          delegationLogId={delegationLog.id}
          entry={wetInkEntry}
          onClose={() => setWetInkEntry(null)}
          onRecorded={() => {
            setWetInkEntry(null);
            toast('Wet-ink sign-off recorded — entry is active');
            loadDelegationLog();
          }}
        />
      )}
    </div>
  );
}

// Required-docs modal — lists every certification the entry's delegated tasks
// require, and links an existing site/study document into each `missing` row
// via POST .../link-document (the backend auto-advances the entry to
// pending_signatures once the last one is approved).
function RequiredDocsModal({
  delegationLogId,
  entry,
  tasks,
  siteId,
  protocolId,
  onClose,
  onChanged,
}: {
  delegationLogId: string;
  entry: DelegationEntry;
  tasks: DelegationTask[];
  siteId: string;
  protocolId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [docs, setDocs] = useState<DelegationRequiredDoc[]>(entry.required_docs || []);
  const [candidates, setCandidates] = useState<Record<string, SearchResult[]>>({});
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const memberName = entry.user?.full_name || entry.external_name || 'this team member';
  const missingCount = docs.filter((d) => d.status === 'missing').length;

  // Load candidate documents per required type (site-scoped: certs are
  // usually site documents, and link-document validates same-site).
  useEffect(() => {
    const types = Array.from(new Set(docs.filter((d) => d.status === 'missing').map((d) => d.document_type)));
    if (types.length === 0) {
      setCandidatesLoading(false);
      return;
    }
    let cancelled = false;
    setCandidatesLoading(true);
    Promise.all(
      types.map((type) =>
        eregApi
          .searchDocuments({ query: '', site_id: siteId, document_type: type, limit: 50 })
          .then((r) => [type, r.results] as const)
          .catch(() => [type, []] as const)
      )
    )
      .then((pairs) => {
        if (!cancelled) setCandidates(Object.fromEntries(pairs));
      })
      .finally(() => {
        if (!cancelled) setCandidatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const taskLabel = (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    return task ? `Task ${task.task_number} — ${task.task_description}` : 'Delegated task';
  };

  const handleLink = async (doc: DelegationRequiredDoc) => {
    const documentId = selections[doc.id];
    if (!documentId) return;
    setLinkingId(doc.id);
    setError(null);
    try {
      await eregApi.linkDocumentToDelegation(delegationLogId, entry.id, doc.id, documentId);
      const linkedName = candidates[doc.document_type]?.find((c) => c.id === documentId)?.document_name;
      setDocs((prev) =>
        prev.map((d) => (d.id === doc.id ? { ...d, status: 'approved', document_id: documentId } : d))
      );
      toast(`Linked ${linkedName || 'document'}`);
      onChanged(); // background refresh — flips the entry to Pending Signatures after the last link
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to link the document');
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Required Documents</h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {memberName} — the delegated tasks require these certifications on file.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {missingCount === 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
              <CheckCircle size={16} />
              All required documents are linked — the entry is ready for sign-offs.
            </div>
          )}

          {docs.map((doc) => {
            const options = candidates[doc.document_type] || [];
            return (
              <div
                key={doc.id}
                className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{doc.document_type}</p>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">{taskLabel(doc.task_id)}</p>
                  </div>
                  {doc.status === 'approved' ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
                      <CheckCircle size={12} />
                      Linked
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                      <AlertCircle size={12} />
                      Missing
                    </span>
                  )}
                </div>

                {doc.status === 'missing' && (
                  <div className="mt-3">
                    {candidatesLoading ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <Loader2 size={14} className="animate-spin text-primary-600 dark:text-primary-400" />
                        Looking for {doc.document_type} documents…
                      </div>
                    ) : options.length === 0 ? (
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        No {doc.document_type} documents at this site yet —{' '}
                        <Link
                          to={`/ereg/studies/${protocolId}/upload?document_type=${encodeURIComponent(doc.document_type)}`}
                          className="font-medium underline"
                        >
                          upload one
                        </Link>{' '}
                        first, then link it here.
                      </p>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={selections[doc.id] || ''}
                          onChange={(e) => setSelections((prev) => ({ ...prev, [doc.id]: e.target.value }))}
                          className={`min-w-0 flex-1 ${fieldCls}`}
                        >
                          <option value="">Select a document…</option>
                          {options.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.document_name}
                              {opt.folder?.path ? ` (${opt.folder.path})` : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleLink(doc)}
                          disabled={!selections[doc.id] || linkingId === doc.id}
                          className={primaryBtnCls}
                        >
                          {linkingId === doc.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Link2 size={14} />
                          )}
                          {linkingId === doc.id ? 'Linking…' : 'Link'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 p-4 dark:border-gray-700">
          <button onClick={onClose} className={secondaryBtnCls}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// Sign-off Modal — password re-auth ceremony for delegation sign-offs
// (start roles + Phase-3 end-of-delegation roles).
function SignOffModal({
  delegationLogId,
  entry,
  role,
  onClose,
  onSuccess,
}: {
  delegationLogId: string;
  entry: DelegationEntry;
  role: DelegationSignRole;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const memberName = entry.user?.full_name || entry.external_name || 'team member';
  // Preview only — the backend writes the authoritative meaning string
  // (backend/src/routes/ereg.js delegation sign route); keep in sync.
  const MEANINGS: Record<DelegationSignRole, string> = {
    team_member: 'I accept responsibility for the delegated study tasks',
    pi: 'I authorize this delegation of study responsibilities',
    team_member_end: 'I confirm the end of my delegated study responsibilities',
    pi_end: 'I confirm this delegation of study responsibilities has ended',
  };
  const TITLES: Record<DelegationSignRole, string> = {
    team_member: 'Sign Delegation Entry',
    pi: 'Countersign as PI',
    team_member_end: 'Sign End of Delegation',
    pi_end: 'Sign End of Delegation as PI',
  };
  const meaning = MEANINGS[role];

  const handleSign = async () => {
    if (!password) {
      setError('Please enter your password');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await eregApi.signDelegationEntry(delegationLogId, entry.id, { password, role });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to sign');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {TITLES[role]}
          </h2>
        </div>
        <div className="space-y-4 p-4">
          <div className="rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-900/40">
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {role.startsWith('team_member') ? 'Your delegation entry' : `Delegation entry for ${memberName}`}
            </p>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              Meaning of signature: <span className="font-medium text-gray-700 dark:text-gray-300">"{meaning}"</span>
            </p>
            {role === 'pi_end' && entry.status === 'active' && (
              <p className="mt-1 text-amber-700 dark:text-amber-400">
                Signing closes this delegation — the entry becomes Ended.
              </p>
            )}
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
            <div className="flex gap-2">
              <AlertCircle className="flex-shrink-0 text-amber-600 dark:text-amber-400" size={18} />
              <p className="text-sm text-amber-800 dark:text-amber-300">
                This records a legally binding electronic signature. Enter your password to verify your identity.
              </p>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your account password"
              className={`w-full ${fieldCls}`}
              autoFocus
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSign}
              disabled={loading || !password}
              className={primaryBtnCls}
            >
              <PenTool size={14} />
              {loading ? 'Signing...' : 'Sign'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Add Member Modal Component
function AddMemberModal({
  delegationLog,
  roles,
  siteId,
  onClose,
  onSuccess,
}: {
  delegationLog: DelegationLog;
  roles: string[];
  siteId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    user_id: '',
    external_name: '',
    external_email: '',
    role: '',
    role_other: '',
    delegated_task_ids: [] as string[],
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
  });
  const [isExternal, setIsExternal] = useState(false);
  const [siteUsers, setSiteUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    eregApi.getEligibleSigners(siteId)
      .then(setSiteUsers)
      .catch((err) => console.error('Failed to load site users:', err));
  }, [siteId]);

  const handleTaskToggle = (taskId: string) => {
    setFormData(prev => ({
      ...prev,
      delegated_task_ids: prev.delegated_task_ids.includes(taskId)
        ? prev.delegated_task_ids.filter(id => id !== taskId)
        : [...prev.delegated_task_ids, taskId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.role) {
      setError('Please select a role');
      return;
    }

    if (formData.delegated_task_ids.length === 0) {
      setError('Please select at least one task');
      return;
    }

    if (isExternal && (!formData.external_name || !formData.external_email)) {
      setError('Please enter name and email for external team member');
      return;
    }

    if (!isExternal && !formData.user_id) {
      setError('Please select the internal user');
      return;
    }

    setLoading(true);
    try {
      await eregApi.addDelegationEntry(delegationLog.id, {
        user_id: isExternal ? undefined : formData.user_id || undefined,
        external_name: isExternal ? formData.external_name : undefined,
        external_email: isExternal ? formData.external_email : undefined,
        role: formData.role,
        role_other: formData.role === 'Other' ? formData.role_other : undefined,
        delegated_task_ids: formData.delegated_task_ids,
        start_date: formData.start_date,
        end_date: formData.end_date || undefined,
      });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add team member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto dark:bg-gray-800">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add Team Member</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors duration-150 dark:hover:bg-gray-700">
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Internal/External Toggle */}
          <div className="flex gap-4 p-3 bg-gray-50 rounded-lg dark:bg-gray-900/40">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={!isExternal}
                onChange={() => setIsExternal(false)}
                className="text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Internal User</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={isExternal}
                onChange={() => setIsExternal(true)}
                className="text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">External (Sponsor/CRO)</span>
            </label>
          </div>

          {isExternal && (
            <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>
                External members can't sign electronically — this entry will stay listed for the
                record, and their delegation is documented on the printed log (wet-ink).
              </span>
            </div>
          )}

          {/* Internal user picker */}
          {!isExternal && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Team Member *
              </label>
              <select
                value={formData.user_id}
                onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                className={`w-full ${fieldCls}`}
              >
                <option value="">Select user...</option>
                {siteUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* External user fields */}
          {isExternal && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.external_name}
                  onChange={(e) => setFormData({ ...formData, external_name: e.target.value })}
                  className={`w-full ${fieldCls}`}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.external_email}
                  onChange={(e) => setFormData({ ...formData, external_email: e.target.value })}
                  className={`w-full ${fieldCls}`}
                  placeholder="john@sponsor.com"
                />
              </div>
            </>
          )}

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Role *
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className={`w-full ${fieldCls}`}
            >
              <option value="">Select role...</option>
              {roles.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          {formData.role === 'Other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Specify Role
              </label>
              <input
                type="text"
                value={formData.role_other}
                onChange={(e) => setFormData({ ...formData, role_other: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>
          )}

          {/* Task Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Delegated Tasks *
            </label>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 dark:border-gray-700">
              {(delegationLog.tasks || []).map((task) => (
                <label key={task.id} className="flex items-start gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded transition-colors duration-150 dark:hover:bg-gray-700/50">
                  <input
                    type="checkbox"
                    checked={formData.delegated_task_ids.includes(task.id)}
                    onChange={() => handleTaskToggle(task.id)}
                    className="mt-1 rounded text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                  />
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-medium">{task.task_number}.</span> {task.task_description}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date (optional)
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <Plus size={18} />
                  <span>Add Team Member</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Task Editor Modal — diff-based (P-6): rows with ids update in place, new
// rows insert, removed rows delete only when no entry delegates them (the
// backend 409s with the blocking task numbers otherwise).
type EditableTask = {
  id?: string;
  task_number: number;
  task_description: string;
  required_types_text: string; // comma-separated in the editor
};

function TaskEditorModal({
  delegationLogId,
  tasks,
  onClose,
  onSaved,
}: {
  delegationLogId: string;
  tasks: DelegationTask[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<EditableTask[]>(
    [...tasks]
      .sort((a, b) => a.task_number - b.task_number)
      .map((t) => ({
        id: t.id,
        task_number: t.task_number,
        task_description: t.task_description,
        required_types_text: (t.required_document_types || []).join(', '),
      }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateRow = (index: number, patch: Partial<EditableTask>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    const nextNumber = rows.reduce((max, r) => Math.max(max, Number(r.task_number) || 0), 0) + 1;
    setRows((prev) => [...prev, { task_number: nextNumber, task_description: '', required_types_text: '' }]);
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await eregApi.updateDelegationTasks(
        delegationLogId,
        rows.map((r) => ({
          id: r.id,
          task_number: Number(r.task_number),
          task_description: r.task_description.trim(),
          required_document_types: r.required_types_text
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        }))
      );
      onSaved();
    } catch (err: any) {
      console.error('Failed to save tasks:', err);
      setError(err?.response?.data?.error || 'Failed to save the tasks');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit Delegated Tasks</h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              Tasks delegated to team members can be edited but not removed. Required documents are
              comma-separated eReg document types.
            </p>
          </div>
          <button onClick={onClose} className="rounded p-1 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700" aria-label="Close">
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="flex-1 space-y-2 overflow-y-auto p-4">
          {rows.map((row, index) => (
            <div key={row.id || `new-${index}`} className="flex items-start gap-2 rounded-lg border border-gray-200 p-2.5 dark:border-gray-700">
              <input
                type="number"
                min={1}
                value={row.task_number}
                onChange={(e) => updateRow(index, { task_number: Number(e.target.value) })}
                aria-label="Task number"
                className={`w-16 ${fieldCls}`}
              />
              <div className="min-w-0 flex-1 space-y-1.5">
                <input
                  type="text"
                  value={row.task_description}
                  onChange={(e) => updateRow(index, { task_description: e.target.value })}
                  placeholder="Task description"
                  aria-label="Task description"
                  className={`w-full ${fieldCls}`}
                />
                <input
                  type="text"
                  value={row.required_types_text}
                  onChange={(e) => updateRow(index, { required_types_text: e.target.value })}
                  placeholder="Required documents (e.g. GCP Certificate, Medical License)"
                  aria-label="Required document types"
                  className={`w-full ${fieldCls}`}
                />
              </div>
              <button
                type="button"
                onClick={() => removeRow(index)}
                title="Remove task (blocked if delegated to a team member)"
                aria-label={`Remove task ${row.task_number}`}
                className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-red-500 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addRow} className={secondaryBtnCls}>
            <Plus size={14} />
            Add task
          </button>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 p-4 dark:border-gray-700">
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || rows.length === 0 || rows.some((r) => !r.task_description.trim() || !Number.isInteger(Number(r.task_number)) || Number(r.task_number) < 1)}
            className={primaryBtnCls}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            {saving ? 'Saving…' : 'Save Tasks'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Edit Entry Modal — pre-sign-off entries can change role/tasks/dates; once
// any sign-off exists only the end date stays editable (the backend enforces
// this with EREG_ENTRY_LOCKED; the modal explains it up front).
function EditEntryModal({
  delegationLogId,
  entry,
  tasks,
  roles,
  onClose,
  onSaved,
}: {
  delegationLogId: string;
  entry: DelegationEntry;
  tasks: DelegationTask[];
  roles: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const locked = Boolean(
    entry.team_member_start_signature_id || entry.pi_start_signature_id
    || entry.team_member_end_signature_id || entry.pi_end_signature_id
    || entry.wet_ink_recorded_at
  );
  const [role, setRole] = useState(entry.role);
  const [roleOther, setRoleOther] = useState(entry.role_other || '');
  const [taskIds, setTaskIds] = useState<string[]>(entry.delegated_task_ids || []);
  const [startDate, setStartDate] = useState(entry.start_date || '');
  const [endDate, setEndDate] = useState(entry.end_date || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleTask = (taskId: string) => {
    setTaskIds((prev) => (prev.includes(taskId) ? prev.filter((t) => t !== taskId) : [...prev, taskId]));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = { end_date: endDate || null };
      if (!locked) {
        payload.role = role;
        payload.role_other = role === 'Other' ? roleOther : null;
        payload.delegated_task_ids = taskIds;
        payload.start_date = startDate;
      }
      await eregApi.updateDelegationEntry(delegationLogId, entry.id, payload as any);
      onSaved();
    } catch (err: any) {
      console.error('Failed to update entry:', err);
      setError(err?.response?.data?.error || 'Failed to update the entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit Entry</h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {entry.user?.full_name || entry.external_name || 'Team member'}
            </p>
          </div>
          <button onClick={onClose} className="rounded p-1 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700" aria-label="Close">
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSave} className="flex-1 space-y-4 overflow-y-auto p-4">
          {locked && (
            <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>
                This entry carries sign-off evidence — the role, tasks, and start date are locked.
                To re-delegate, end this entry and add a new one.
              </span>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={locked}
              className={`w-full ${fieldCls} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {roles.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          {role === 'Other' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Specify Role</label>
              <input
                type="text"
                value={roleOther}
                onChange={(e) => setRoleOther(e.target.value)}
                disabled={locked}
                className={`w-full ${fieldCls} disabled:cursor-not-allowed disabled:opacity-60`}
              />
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Delegated Tasks</label>
            <div className="grid max-h-44 grid-cols-2 gap-2 overflow-y-auto rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              {tasks.map((task) => (
                <label key={task.id} className={`flex items-start gap-2 rounded p-2 transition-colors duration-150 ${locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
                  <input
                    type="checkbox"
                    checked={taskIds.includes(task.id)}
                    onChange={() => toggleTask(task.id)}
                    disabled={locked}
                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-medium">{task.task_number}.</span> {task.task_description}
                  </span>
                </label>
              ))}
            </div>
            {!locked && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                Changing tasks re-derives the required documents for this entry.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={locked}
                className={`w-full ${fieldCls} disabled:cursor-not-allowed disabled:opacity-60`}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={`w-full ${fieldCls}`}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || (!locked && (taskIds.length === 0 || !startDate))}
              className={primaryBtnCls}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// End Entry Modal — the administrative end for departed staff (no signature
// required; the PI can add end sign-off evidence any time after).
function EndEntryModal({
  delegationLogId,
  entry,
  onClose,
  onEnded,
}: {
  delegationLogId: string;
  entry: DelegationEntry;
  onClose: () => void;
  onEnded: () => void;
}) {
  const [endDate, setEndDate] = useState(entry.end_date || new Date().toISOString().slice(0, 10));
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const memberName = entry.user?.full_name || entry.external_name || 'this team member';

  const handleEnd = async () => {
    setEnding(true);
    setError(null);
    try {
      await eregApi.updateDelegationEntry(delegationLogId, entry.id, {
        status: 'ended',
        end_date: endDate,
      } as any);
      onEnded();
    } catch (err: any) {
      console.error('Failed to end delegation:', err);
      setError(err?.response?.data?.error || 'Failed to end the delegation');
    } finally {
      setEnding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">End Delegation</h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          End <span className="font-medium text-gray-900 dark:text-gray-100">{memberName}</span>'s delegation?
          The entry becomes <span className="font-medium">Ended</span> and stays on the log; end-of-delegation
          sign-offs can be added afterward.
        </p>
        <div className="mt-3">
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={`w-full ${fieldCls}`}
          />
        </div>
        {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button onClick={handleEnd} disabled={ending || !endDate} className={dangerBtnCls}>
            <UserMinus size={14} />
            {ending ? 'Ending…' : 'End Delegation'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Wet-ink Modal (TMF-7) — records that the external member's sign-offs
// happened on the printed log; the entry activates.
function WetInkModal({
  delegationLogId,
  entry,
  onClose,
  onRecorded,
}: {
  delegationLogId: string;
  entry: DelegationEntry;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const [note, setNote] = useState('');
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRecord = async () => {
    setRecording(true);
    setError(null);
    try {
      await eregApi.recordWetInkSignOff(delegationLogId, entry.id, note.trim() || undefined);
      onRecorded();
    } catch (err: any) {
      console.error('Failed to record wet-ink sign-off:', err);
      setError(err?.response?.data?.error || 'Failed to record the wet-ink sign-off');
    } finally {
      setRecording(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Record Wet-Ink Sign-Off</h2>
        </div>
        <div className="space-y-4 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-900 dark:text-gray-100">{entry.external_name || 'This external member'}</span>{' '}
            can't sign electronically. Record that the delegation sign-offs were completed on the
            printed log — the entry becomes <span className="font-medium">Active</span> and the PDF
            export cites this recording.
          </p>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder='e.g. "Signed printed log page 2, filed in Regulatory"'
              className={`w-full ${fieldCls}`}
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button onClick={handleRecord} disabled={recording} className={primaryBtnCls}>
              <ClipboardSignature size={14} />
              {recording ? 'Recording…' : 'Record Sign-Off'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DelegationLogPage;
